import { ButtonBase } from "./Button";
import {
  FieldError,
  FieldLabel,
  FormControl,
  FormControlContent,
  FormField,
  Input,
  Textarea as DesignSystemTextarea,
} from "./Form";
import {
  designSystemTokenValues as token,
  designSystemVars as vars,
} from "../tokens";
import styled, { css, keyframes } from "styled-components";

type ErrorProp = {
  // haveError?: string;
  haveError?: boolean;
  disabled?: boolean;
  width?: number;
  $bgColor?: string;
  $disabled?: boolean;
  $focusVisible?: boolean;
  $active?: boolean;
  $padding?: string;
  $margin?: string;
};

export const StyledInputBox = styled(FormField)<ErrorProp>`
  align-self: stretch;
  background-color: ${(props) =>
    props.$disabled ? vars.color.control.backgroundDisabled : ""};
  cursor: text;
`;

export const StyledErrorMessageWrapper = styled(FieldError)`
  width: 401px;
  height: 30px;
  margin-top: -${vars.spacing.xs};

  p {
    margin: 0;
  }
`;

export const StyledLabel = styled(FieldLabel).withConfig({
  shouldForwardProp: (prop) => prop !== "haveError",
})<ErrorProp>`
  color: ${(props) =>
    props.haveError
      ? token.color.control.borderError
      : token.color.text.primary};

  font-family: ${token.typography.family.sans};
  font-size: ${token.typography.size.sm};
  font-style: normal;
  font-weight: ${token.typography.weight.medium};
  line-height: ${token.typography.lineHeight.sm};
`;

export const StyledInputContainer = styled(FormControl).withConfig({
  shouldForwardProp: (prop) => prop !== "haveError",
})<ErrorProp>`
  gap: ${vars.spacing.sm};
  padding: ${(props) =>
    props.$padding || `${vars.spacing.xs} ${vars.spacing.md}`};
  margin: ${(props) => props.$margin || "0"};
  align-self: stretch;
  border-radius: ${vars.radius.sm};
  border: ${(props) =>
    `${props.haveError ? vars.borderWidth.medium : vars.borderWidth.thin} solid ${
      props.haveError
        ? vars.color.control.borderError
        : vars.color.control.border
    }`};
  background-color: ${(props) =>
    props.$disabled
      ? vars.color.control.backgroundDisabled
      : vars.color.control.background};
  width: ${(props) => (props.width ? `${props.width}px` : "")};
  cursor: text;

  &:focus-within {
    border: ${(props) =>
      props.haveError
        ? `${vars.borderWidth.medium} solid ${vars.color.control.borderError}`
        : `${vars.borderWidth.medium} solid ${vars.color.border.accent}`};
  }
  &:focus-within ${StyledLabel} {
    color: ${(props) =>
      props.haveError
        ? vars.color.control.borderError
        : vars.color.text.primary};
  }

  ${(props) =>
    props.$focusVisible &&
    css`
      outline: none;
      box-shadow: ${vars.shadow.focus};
    `}

  &:hover {
    cursor: text;
  }
`;

export const StyledInputContentWrapper = styled(FormControlContent)<{
  $disabled: boolean;
}>`
  background-color: ${(props) =>
    props.$disabled
      ? vars.color.control.backgroundDisabled
      : vars.color.control.background};
`;

export const StyledTextInput = styled(Input)<{
  $disabled: boolean;
  $hasPasswordIcon?: boolean;
}>`
  display: flex;
  border: none;
  outline: none;
  height: 32px;
  align-items: center;
  gap: var(--inzeloop-ds-spacing-sm);
  flex-shrink: 0;
  align-self: stretch;
  width: 100%;
  box-sizing: border-box;
  padding-right: ${({ $hasPasswordIcon }) =>
    $hasPasswordIcon ? "var(--inzeloop-ds-spacing-36px)" : "0"};

  background-color: ${(props) =>
    props.$disabled
      ? vars.color.control.backgroundDisabled
      : vars.color.control.background};

  color: ${vars.color.control.text};

  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-style: normal;
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};

  &:focus-visible {
    outline: none;
  }
`;

export const StyledPasswordIconWrapper = styled(ButtonBase)`
  position: absolute;
  right: 0;
  bottom: 8px;

  width: 24px;
  height: 24px;

  border: none;
  background: var(--inzeloop-ds-color-background-transparent);
  padding: 0;
  cursor: default;

  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: none;
    border-radius: ${vars.radius.xs};
    box-shadow: ${vars.shadow.focus};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const StyledTextareaWrapper = styled(FormControl)<{
  $haveError?: boolean;
  height?: string;
  $disabled: boolean;
  $focusVisible?: boolean;
  $resizable?: boolean;
  $variant?: "default" | "prompt";
  $hasAttachments?: boolean;
  $processing?: boolean;
}>`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: ${({ $variant }) => ($variant === "prompt" ? "240px" : "auto")};
  border-radius: ${({ $variant }) =>
    $variant === "prompt" ? vars.radius.lg : vars.radius.sm};
  gap: ${vars.spacing.sm};
  resize: ${({ $resizable }) => ($resizable ? "vertical" : "none")};
  overflow: auto;
  border: ${(props) =>
    `${props.$haveError || props.$variant === "prompt" ? vars.borderWidth.medium : vars.borderWidth.thin} solid ${
      props.$haveError
        ? vars.color.control.borderError
        : props.$variant === "prompt"
          ? vars.color.border.strong
          : vars.color.control.border
    }`};
  background-color: ${(props) =>
    props.$disabled
      ? vars.color.control.backgroundDisabled
      : props.$variant === "prompt"
        ? vars.color.background.subtle
        : vars.color.control.background};
  padding: ${({ $variant }) =>
    $variant === "prompt"
      ? vars.spacing.xl
      : `${vars.spacing.sm} ${vars.spacing.sm} ${vars.spacing.md} ${vars.spacing.md}`};

  ${({ $variant }) =>
    $variant === "prompt" &&
    css`
      padding-bottom: calc(${vars.spacing.xl} + ${vars.spacing.lg});

      @media (max-width: 600px) {
        padding-bottom: calc(${vars.spacing.xl} + ${vars.spacing.px36});
      }
    `}

  > textarea {
    flex: ${({ $variant }) => ($variant === "prompt" ? "1 1 auto" : "initial")};
  }

  ${({ $variant, $hasAttachments }) =>
    $variant === "prompt" &&
    $hasAttachments &&
    css`
      min-height: 314px;
      gap: ${vars.spacing.lg};
      padding-bottom: calc(
        ${vars.spacing["2xl"]} + ${vars.spacing.lg} + ${vars.spacing.xxs}
      );

      > textarea {
        min-height: 188px;
        flex-shrink: 0;
      }

      > div {
        flex-shrink: 0;
        width: 100%;
      }
    `}

  &:focus-within {
    border: ${(props) =>
      props.$haveError
        ? `${vars.borderWidth.medium} solid ${vars.color.control.borderError}`
        : `${vars.borderWidth.medium} solid ${vars.color.border.accent}`};
  }
  &:focus-within ${StyledLabel} {
    color: ${(props) =>
      props.$haveError
        ? vars.color.control.borderError
        : vars.color.text.primary};
  }

  ${(props) =>
    props.$focusVisible &&
    css`
      outline: none;
      box-shadow: ${vars.shadow.focus};
    `}
  ${({ $processing }) =>
    $processing &&
    css`
      border: ${vars.borderWidth.thick} solid transparent;
      background:
        linear-gradient(
            ${vars.color.background.subtle},
            ${vars.color.background.subtle}
          )
          padding-box,
        ${vars.color.action.aiGradient} border-box;
      &:focus-within {
        border: ${vars.borderWidth.thick} solid transparent;
      }
    `}
`;

export const StyledTopRow = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

export const StyledLabelInfoGroup = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
  height: 24px;
  gap: ${vars.spacing.xs};
`;

export const StyledInfoWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: none;
    border-radius: ${vars.radius["2xs"]};
    border-color: ${vars.color.border.accent};
    box-shadow: ${vars.shadow.focus};
  }

  svg {
    width: 21px;
    height: 21px;
  }
`;

export const StyledEditIconWrapper = styled.div`
  position: absolute;
  top: -2px;
  right: 5px;

  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  padding: var(--inzeloop-ds-spacing-xs);
`;

export const StyledTextarea = styled(DesignSystemTextarea).withConfig({
  shouldForwardProp: (prop) => prop !== "isExceeded",
})<{
  isExceeded?: boolean;
  $disabled: boolean;
  $processing?: boolean;
}>`
  min-height: 25px;
  border: none;
  outline: none;
  resize: none;
  color: ${vars.color.control.text};
  background-color: var(--inzeloop-ds-color-background-transparent);
  padding-right: var(--inzeloop-ds-spacing-30px);
  overflow-y: auto;
  background-color: ${(props) =>
    props.$disabled ? vars.color.control.backgroundDisabled : ""};

  &::-webkit-resizer {
    display: none;
  }

  &::-webkit-scrollbar {
    width: ${vars.spacing.xs};
  }

  &::-webkit-scrollbar-track {
    border-radius: ${vars.radius["2xs"]};
    margin: ${vars.spacing.xs} 0;
  }

  &::-webkit-scrollbar-thumb {
    background: ${vars.color.control.scrollbar};
    border-radius: ${vars.radius["2xs"]};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${vars.color.control.scrollbarHover};
  }

  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-style: normal;
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};
  ${({ $processing }) =>
    $processing &&
    css`
      font-weight: ${vars.typography.weight.regular};
      line-height: ${vars.typography.lineHeight.lg};
      &:disabled {
        color: ${vars.color.control.text};
        -webkit-text-fill-color: ${vars.color.control.text};
      }
    `}
`;

export const StyledCounter = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "isExceeded",
})<{
  isExceeded?: boolean;
}>`
  color: ${({ isExceeded }) =>
    isExceeded ? vars.color.control.errorText : vars.color.text.secondary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.xs};
  font-style: normal;
  font-weight: var(--inzeloop-ds-font-weight-regular);
`;

export const StyledSelectedArea = styled.div`
  padding: var(--inzeloop-ds-spacing-xs) var(--inzeloop-ds-spacing-sm)
    var(--inzeloop-ds-spacing-6px) var(--inzeloop-ds-spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--inzeloop-ds-spacing-xs);
  width: 100%;
  box-sizing: border-box;
`;

export const StyledTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--inzeloop-ds-spacing-6px);
  width: 100%;
`;

export const StyledCursorRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 20px;
`;

export const StyledTags = styled.div`
  display: flex;
  padding: var(--inzeloop-ds-spacing-sm);
  align-items: center;
  gap: var(--inzeloop-ds-spacing-sm);
  border-radius: var(--inzeloop-ds-radius-sm);
  border: ${vars.borderWidth.thin} solid ${vars.color.border.strong};
`;

const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
`;

export const StyledSelectedValue = styled.span<{
  $hasCursor?: boolean;
  $isPlaceholder?: boolean;
}>`
  display: inline-block;
  min-width: 8px;
  font-size: var(--inzeloop-ds-font-size-14px);
  color: ${({ $isPlaceholder }) =>
    $isPlaceholder
      ? vars.color.control.placeholder
      : vars.color.control.textSubdued};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $hasCursor }) =>
    $hasCursor &&
    css`
      &::after {
        content: "|";
        display: inline-block;
        animation: ${blink} 1s step-start infinite;
        margin-left: var(--inzeloop-ds-spacing-1px);
      }
    `}
`;
